import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Panel, Skeleton, Empty, Icon } from '../../components/ui';
import { getOutreachAreas } from '../../utils/api';
import { fmtDateTime, fmtNumber, fmtPct, loadError } from '../utils';

// One age band from the Census tables: residents, and how many of them have the named coverage.
function AgeBand({ label, people, covered, share, what }) {
  if (people == null && covered == null) return null;
  return <div><dt>{label}</dt><dd>{fmtNumber(covered)} {what}{share != null ? <div className="ops-cell-sub">{fmtPct(share)} of {fmtNumber(people)} residents</div> : null}</dd></div>;
}

// Residents 55 and over on Medicaid, as a range: the exact 65 and over count
// up to that count plus everyone 55 to 64 with public coverage of any kind.
function FiftyFivePlus({ c }) {
  if (c.adults55Plus == null) return null;
  const known = c.adults55PlusMedicaidKnown; const upTo = c.adults55PlusMedicaidUpTo;
  const range = known != null && upTo != null && upTo !== known ? `${fmtNumber(known)} to ${fmtNumber(upTo)}` : fmtNumber(known != null ? known : upTo);
  const shares = c.adults55PlusMedicaidKnownShare != null && c.adults55PlusMedicaidUpToShare != null ? `${fmtPct(c.adults55PlusMedicaidKnownShare)} to ${fmtPct(c.adults55PlusMedicaidUpToShare)} of ` : '';
  return <div><dt>55 and over</dt><dd>{range} on Medicaid<div className="ops-cell-sub">{shares}{fmtNumber(c.adults55Plus)} residents</div></dd></div>;
}

function Census({ c }) {
  if (!c) return <div className="ops-help">No census figures yet. They are collected on the next research run when the Census source is enabled.</div>;
  return (
    <>
      <dl className="ops-dl ops-dl--4">
        <div><dt>Population</dt><dd>{fmtNumber(c.population)}</dd></div>
        <div><dt>Medicaid covered</dt><dd>{fmtNumber(c.medicaidCovered)}{c.medicaidShare != null ? <div className="ops-cell-sub">{fmtPct(c.medicaidShare)} of residents</div> : null}</dd></div>
        <div><dt>Median income</dt><dd>{c.medianHouseholdIncome != null ? `${fmtNumber(c.medianHouseholdIncome)}` : <span className="muted">n/a</span>}</dd></div>
        <div><dt>Median age</dt><dd>{c.medianAge != null ? c.medianAge : <span className="muted">n/a</span>}</dd></div>
      </dl>
      <div className="ops-label" style={{ marginTop: 10 }}>Medicaid, 55 and over</div>
      <dl className="ops-dl ops-dl--4" style={{ marginTop: 4 }}>
        <FiftyFivePlus c={c} />
        <AgeBand label="55 to 64" people={c.adults55to64} covered={c.adults55to64Public} share={c.adults55to64PublicShare} what="with public coverage" />
        <AgeBand label="65 and over" people={c.seniors65Plus} covered={c.seniors65PlusMedicaid} share={c.seniors65PlusMedicaidShare} what="on Medicaid" />
      </dl>
      <div className="ops-help" style={{ marginTop: 4 }}>The Census 5-year release counts Medicaid at 65 and over but not at 55 to 64, where it only counts public coverage of any kind (Medicaid, Medicare or VA). The 55 and over range runs from the known 65 and over count to that count plus the 55 to 64 public coverage figure.</div>
      <div className="ops-label" style={{ marginTop: 10 }}>Younger ages, for context</div>
      <dl className="ops-dl ops-dl--4" style={{ marginTop: 4 }}>
        <AgeBand label="Under 19" people={c.under19} covered={c.under19Medicaid} share={c.under19MedicaidShare} what="on Medicaid" />
        <AgeBand label="19 to 64" people={c.adults19to64} covered={c.adults19to64Medicaid} share={c.adults19to64MedicaidShare} what="on Medicaid" />
      </dl>
    </>
  );
}

export default function AreasPage() {
  const [areas, setAreas] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getOutreachAreas().then(r => setAreas(r.areas || [])).catch(e => { setError(loadError(e, 'Could not load area intelligence')); setAreas([]); });
  }, []);

  return (
    <>
      <PageHeader title="Area intelligence" subtitle="Public census figures and office coverage for each community, with the agent's read on where outreach is most worthwhile. Figures describe populations, never individual people." />
      {error && <div className="ops-error">{error}</div>}
      {!areas ? <Panel><Skeleton rows={6} /></Panel> : !areas.length ? <Panel><Empty title="No areas are configured. Add them in Settings." /></Panel> : (
        <div style={{ display: 'grid', gap: 18 }}>
          {areas.map(a => {
            const counts = a.counts || {};
            const intel = a.intelligence;
            const source = a.census?.source;
            return (
              <Panel key={a.areaKey} title={`${a.label}${a.primary ? ' (primary)' : ''}`} action={<Link className="ops-link" to={`/outreach/offices?area=${encodeURIComponent(a.areaKey)}`}>View offices <Icon name="chevron" size={12} /></Link>}>
                <div className="ops-stat-row" style={{ marginBottom: 14 }}>
                  <div className="ops-stat"><b>{counts.offices ?? 0}</b><span>Offices</span></div>
                  <div className="ops-stat"><b>{counts.byPriority?.P1 ?? 0}</b><span>High priority</span></div>
                  <div className="ops-stat"><b>{counts.withMedicaidEvidence ?? 0}</b><span>Medicaid evidence</span></div>
                  <div className="ops-stat"><b>{counts.contacted ?? 0}</b><span>Contacted</span></div>
                  <div className="ops-stat"><b>{counts.engaged ?? 0}</b><span>Engaged</span></div>
                </div>
                <Census c={a.census} />
                {source && <div className="ops-help" style={{ marginTop: 6 }}>Source: {source.name}{source.year ? ` ${source.year}` : ''}.</div>}
                {!!counts.byPracticeType && Object.keys(counts.byPracticeType).length > 0 && (
                  <div className="ops-help" style={{ marginTop: 6 }}>By practice type: {Object.entries(counts.byPracticeType).sort((x, y) => y[1] - x[1]).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>
                )}
                <div className="ops-form-section" style={{ marginTop: 16 }}>
                  <h3>Research and next actions</h3>
                  {!intel ? <div className="ops-help">No area analysis yet. It is produced during a research run when Area intelligence is enabled in Settings.</div> : (
                    <>
                      {intel.summary && <p style={{ margin: '0 0 10px', fontSize: 13.5 }}>{intel.summary}</p>}
                      <div className="ops-grid-2">
                        <div>
                          <div className="ops-label">Opportunities</div>
                          {intel.opportunities?.length ? <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>{intel.opportunities.map((x, i) => <li key={i}>{x}</li>)}</ul> : <div className="ops-help">None listed.</div>}
                        </div>
                        <div>
                          <div className="ops-label">Cautions</div>
                          {intel.cautions?.length ? <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>{intel.cautions.map((x, i) => <li key={i}>{x}</li>)}</ul> : <div className="ops-help">None listed.</div>}
                        </div>
                      </div>
                      {intel.recommendedFocus && <div className="ops-note" style={{ marginTop: 10 }}><strong>Recommended focus.</strong> {intel.recommendedFocus}</div>}
                      <div className="ops-help" style={{ marginTop: 8 }}>{intel.source === 'cache' ? 'Reused from cache' : `Analyzed by ${intel.modelId || 'model'}`}, prompt {intel.promptVersion}, {fmtDateTime(intel.analyzedAt)}.</div>
                    </>
                  )}
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </>
  );
}
